// « Ma gamme » (module optionnel, migration 046) — définitions et calculs PURS partagés par le serveur et l'interface.
//  - protocoles de communication (compatibilités) et leur reconnaissance dans un texte (ligne de CCTP, de DPGF, recherche) ;
//  - documents de conformité attendus et leur état (manquant, expiré, à renouveler) ;
//  - état d'un accord de distribution (en vigueur, fin proche, échu, à venir) ;
//  - prix d'achat effectif d'un produit : palier de l'accord > prix ou remise de l'accord > remise négociée du fournisseur > prix de base.
// Aucune valeur inventée : un prix inconnu reste null (« À renseigner »).
import Decimal from 'decimal.js';

export const PROTOCOLES = ['knx', 'modbus_rtu', 'modbus_tcp', 'bacnet_ip', 'bacnet_mstp', 'mbus', 'dali', 'lon', 'ip', 'enocean', 'zigbee', 'autre'] as const;
export type Protocole = typeof PROTOCOLES[number];
export const LIBELLES_PROTOCOLES: Record<Protocole, string> = {
  knx: 'KNX', modbus_rtu: 'Modbus RTU', modbus_tcp: 'Modbus TCP', bacnet_ip: 'BACnet IP', bacnet_mstp: 'BACnet MS/TP', mbus: 'M-Bus',
  dali: 'DALI', lon: 'LON', ip: 'IP (Ethernet)', enocean: 'EnOcean', zigbee: 'Zigbee', autre: 'Autre',
};

export const NATURES = ['maison', 'distribue'] as const;
export type NatureProduit = typeof NATURES[number];
export const LIBELLES_NATURES: Record<NatureProduit, string> = { maison: 'Produit maison', distribue: 'Produit distribué' };

export const NATURES_DOCUMENTS = ['photo', 'fiche_technique', 'declaration_ue', 'documentation_technique', 'notice', 'certificat', 'eco_organisme', 'autre'] as const;
export type NatureDocument = typeof NATURES_DOCUMENTS[number];
export const LIBELLES_DOCUMENTS: Record<NatureDocument, string> = {
  photo: 'Photo', fiche_technique: 'Fiche technique', declaration_ue: 'Déclaration UE de conformité',
  documentation_technique: 'Documentation technique', notice: 'Notice (en français)', certificat: 'Certificat ou rapport d’essai',
  eco_organisme: 'Attestation d’adhésion à un éco-organisme (DEEE / REP) — facultative', autre: 'Autre document',
};
/** Documents suivis (docs/REGLEMENTATION-MA-GAMME.md) : le fabricant (produit maison) porte la déclaration UE de conformité, la
 *  documentation technique et la notice ; le distributeur vérifie la présence de la déclaration et de la notice. Seuls ces
 *  documents donnent un état « manquant », « à renouveler » ou « expiré » et une alerte. */
export const DOCUMENTS_SUIVIS: Record<NatureProduit, NatureDocument[]> = {
  maison: ['declaration_ue', 'documentation_technique', 'notice'],
  distribue: ['declaration_ue', 'notice'],
};
/**
 * Documents FACULTATIFS : se joignent comme les autres, jamais réclamés, jamais d'alerte ni de blocage. L'attestation d'éco-organisme
 * (DEEE / REP) en fait partie, y compris pour un produit maison (décision du dirigeant du 31/10/2026 : en général, le client ne
 * fabrique pas lui-même ; son fournisseur lui fait un prix privilégié et les produits portent déjà leurs normes de base).
 */
export const DOCUMENTS_FACULTATIFS: NatureDocument[] = NATURES_DOCUMENTS.filter((n) => !DOCUMENTS_SUIVIS.maison.includes(n) && !DOCUMENTS_SUIVIS.distribue.includes(n));
/** Délai d'alerte avant l'échéance d'un DOCUMENT de conformité (« à renouveler ») : affiché, jamais caché. */
export const ALERTE_DOCUMENTS_JOURS = 30;
/**
 * Délai d'alerte avant la FIN D'UN ACCORD de distribution : réglable par société (décision du dirigeant du 31/10/2026),
 * organizations.settings → gamme.alerte_accord_jours ; valeur par défaut et bornes ci-dessous (aucune autre valeur en dur).
 */
export const DELAI_ACCORD = { defaut: 30, min: 1, max: 365 } as const;
/** Délai réglé lu dans le JSON des réglages : entier dans les bornes, sinon la valeur par défaut. */
export function delaiAccordLu(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d{1,4}$/.test(v) ? Number(v) : NaN;
  return Number.isInteger(n) && n >= DELAI_ACCORD.min && n <= DELAI_ACCORD.max ? n : DELAI_ACCORD.defaut;
}

// ─── Dates ─────────────────────────────────────────────────────────────────────────────────────
export const joursEntre = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

// ─── Conformité ────────────────────────────────────────────────────────────────────────────────
export type EtatDocument = 'present' | 'a_renouveler' | 'expire' | 'manquant';
export interface DocumentDate { nature: string; valide_jusqu_au: string | null }
export interface EtatConformite { nature: NatureDocument; libelle: string; etat: EtatDocument; echeance: string | null; jours: number | null }

export function etatConformite(nature: NatureProduit, documents: DocumentDate[], aujourdhui: string, alerte = ALERTE_DOCUMENTS_JOURS): EtatConformite[] {
  return DOCUMENTS_SUIVIS[nature].map((n) => {
    const docs = documents.filter((x) => x.nature === n);
    if (!docs.length) return { nature: n, libelle: LIBELLES_DOCUMENTS[n], etat: 'manquant' as const, echeance: null, jours: null };
    // Le document le plus durable fait foi (sans échéance = valable).
    if (docs.some((x) => !x.valide_jusqu_au)) return { nature: n, libelle: LIBELLES_DOCUMENTS[n], etat: 'present' as const, echeance: null, jours: null };
    const echeance = docs.map((x) => x.valide_jusqu_au!).sort().at(-1)!;
    const jours = joursEntre(aujourdhui, echeance);
    const etat: EtatDocument = jours < 0 ? 'expire' : jours <= alerte ? 'a_renouveler' : 'present';
    return { nature: n, libelle: LIBELLES_DOCUMENTS[n], etat, echeance, jours };
  });
}

// ─── Accords de distribution ───────────────────────────────────────────────────────────────────
export type EtatAccord = 'en_vigueur' | 'fin_proche' | 'echu' | 'a_venir' | 'retire';
export interface AccordDates { id: string; date_debut: string; date_fin: string | null; retire_le?: string | null }

/**
 * État d'un accord à une date (fin proche : échéance dans `alerte` jours au plus, jour d'échéance compris). `alerte` = délai réglé
 * par la société (DELAI_ACCORD) ; sans délai (null), seuls « en vigueur », « échu », « à venir » et « retiré » sont distingués
 * (calcul du prix, choix du fournisseur : la fin proche n'y change rien).
 */
export function etatAccord(a: AccordDates, aujourdhui: string, alerte: number | null): { etat: EtatAccord; jours: number | null } {
  if (a.retire_le) return { etat: 'retire', jours: null };
  if (a.date_debut > aujourdhui) return { etat: 'a_venir', jours: joursEntre(aujourdhui, a.date_debut) };
  if (!a.date_fin) return { etat: 'en_vigueur', jours: null };
  const jours = joursEntre(aujourdhui, a.date_fin);
  if (jours < 0) return { etat: 'echu', jours };
  return { etat: alerte !== null && jours <= alerte ? 'fin_proche' : 'en_vigueur', jours };
}

export interface AlerteAccord { accord_id: string; etat: 'fin_proche' | 'echu'; date_fin: string; jours: number }

/**
 * Alerte d'un produit : un accord qui finit bientôt SANS accord suivant qui prend le relais, ou un produit dont le dernier accord est
 * échu sans aucun accord en vigueur ni à venir. Un accord retiré n'alerte jamais.
 */
export function alerteAccords(accords: AccordDates[], aujourdhui: string, alerte: number): AlerteAccord | null {
  const vivants = accords.filter((a) => !a.retire_le);
  const etats = vivants.map((a) => ({ a, ...etatAccord(a, aujourdhui, alerte) }));
  const relais = (fin: string) => vivants.some((b) => b.date_debut <= addJours(fin, 1) && (b.date_fin === null || b.date_fin > fin));
  const proche = etats.filter((e) => e.etat === 'fin_proche' && !relais(e.a.date_fin!)).sort((x, y) => x.a.date_fin!.localeCompare(y.a.date_fin!))[0];
  if (proche) return { accord_id: proche.a.id, etat: 'fin_proche', date_fin: proche.a.date_fin!, jours: proche.jours! };
  if (etats.some((e) => e.etat === 'en_vigueur' || e.etat === 'fin_proche' || e.etat === 'a_venir')) return null;
  const dernier = etats.filter((e) => e.etat === 'echu').sort((x, y) => y.a.date_fin!.localeCompare(x.a.date_fin!))[0];
  return dernier ? { accord_id: dernier.a.id, etat: 'echu', date_fin: dernier.a.date_fin!, jours: dernier.jours! } : null;
}
export function addJours(j: string, n: number) { const d = new Date(`${j}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }

// ─── Prix d'achat effectif ─────────────────────────────────────────────────────────────────────
export interface PalierEntree { quantite_min: string; prix_net: string | null; remise_pct: string | null }
export interface AccordPrix extends AccordDates { prix_net: string | null; remise_pct: string | null; paliers: PalierEntree[] }
export type OriginePrix = 'palier' | 'accord' | 'remise_fournisseur' | 'base';
export interface PrixEffectif { prix: string | null; origine: OriginePrix | null; accord_id: string | null; palier_min: string | null; remise_pct: string | null; detail: string }

const remiser = (base: string, pct: string) => new Decimal(base).mul(new Decimal(100).minus(pct)).div(100).toDecimalPlaces(4, Decimal.ROUND_HALF_UP).toFixed(4);
const nombreFr = (v: string) => new Decimal(v).toFixed().replace('.', ',');

/**
 * Prix d'achat unitaire HT pour une quantité à une date. Ordre (le prix au niveau PRODUIT prime) :
 *   1. accord en vigueur (le plus récent) : palier le plus haut atteint par la quantité (prix net ou remise sur le prix de base) ;
 *   2. sinon prix net ou remise de l'accord ;
 *   3. sinon remise négociée du fournisseur pour ce fabricant ou par défaut (option tarification avancée) appliquée au prix de base ;
 *   4. sinon le prix de base du produit. Une remise sans prix de base connu ne donne AUCUN prix (null, jamais inventé).
 */
export function prixEffectif(o: { prixBase: string | null; accords: AccordPrix[]; quantite: string; aujourdhui: string; remiseFournisseur?: string | null }): PrixEffectif {
  const vides = { accord_id: null, palier_min: null, remise_pct: null };
  const enVigueur = o.accords.filter((a) => etatAccord(a, o.aujourdhui, null).etat === 'en_vigueur')
    .sort((x, y) => y.date_debut.localeCompare(x.date_debut))[0];
  const q = new Decimal(o.quantite || '1');
  if (enVigueur) {
    const palier = [...enVigueur.paliers].filter((p) => q.gte(p.quantite_min)).sort((x, y) => new Decimal(y.quantite_min).comparedTo(x.quantite_min))[0];
    const regle = palier ?? (enVigueur.prix_net !== null || enVigueur.remise_pct !== null ? enVigueur : null);
    if (regle) {
      const origine: OriginePrix = palier ? 'palier' : 'accord';
      const qui = palier ? `palier à partir de ${nombreFr(palier.quantite_min)}` : 'accord de distribution';
      if (regle.prix_net !== null) {
        return { prix: new Decimal(regle.prix_net).toFixed(4), origine, accord_id: enVigueur.id, palier_min: palier?.quantite_min ?? null, remise_pct: null,
          detail: `Prix net — ${qui}` };
      }
      if (o.prixBase === null) {
        return { prix: null, origine, accord_id: enVigueur.id, palier_min: palier?.quantite_min ?? null, remise_pct: regle.remise_pct,
          detail: `Remise de ${nombreFr(regle.remise_pct!)} % (${qui}) : prix de base à renseigner` };
      }
      return { prix: remiser(o.prixBase, regle.remise_pct!), origine, accord_id: enVigueur.id, palier_min: palier?.quantite_min ?? null, remise_pct: regle.remise_pct,
        detail: `Remise de ${nombreFr(regle.remise_pct!)} % — ${qui}` };
    }
  }
  if (o.remiseFournisseur && o.prixBase !== null) {
    return { prix: remiser(o.prixBase, o.remiseFournisseur), origine: 'remise_fournisseur', ...vides, remise_pct: o.remiseFournisseur,
      detail: `Remise négociée du fournisseur : ${nombreFr(o.remiseFournisseur)} %` };
  }
  return o.prixBase === null ? { prix: null, origine: null, ...vides, detail: 'Prix d’achat à renseigner' }
    : { prix: new Decimal(o.prixBase).toFixed(4), origine: 'base', ...vides, detail: 'Prix d’achat de base' };
}

// ─── Compatibilité d'un produit avec un texte (ligne de CCTP / DPGF, recherche) ────────────────
export const normaliser = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const MOTIFS: [Protocole[], RegExp][] = [
  [['knx'], /\bknx\b/],
  [['modbus_rtu'], /\bmodbus\W{0,3}(rtu|rs\W?485)\b/],
  [['modbus_tcp'], /\bmodbus\W{0,3}(tcp|ip)\b/],
  [['modbus_rtu', 'modbus_tcp'], /\bmodbus\b/],
  [['bacnet_ip'], /\bbacnet\W{0,3}ip\b/],
  [['bacnet_mstp'], /\bbacnet\W{0,3}ms\W?tp\b/],
  [['bacnet_ip', 'bacnet_mstp'], /\bbacnet\b/],
  [['mbus'], /\bm\W?bus\b/],
  [['dali'], /\bdali\b/],
  [['lon'], /\blon(works)?\b/],
  [['enocean'], /\benocean\b/],
  [['zigbee'], /\bzigbee\b/],
];
/** Protocoles cités dans un texte. Un « Modbus » sans précision vaut pour RTU et TCP (idem BACnet). « IP » seul n'est pas reconnu (trop vague). */
export function protocolesCites(texte: string): Set<Protocole> {
  const t = normaliser(texte);
  const r = new Set<Protocole>();
  for (const [ps, m] of MOTIFS) if (m.test(t)) ps.forEach((p) => r.add(p));
  return r;
}

const MOTS_VIDES = new Set(['avec', 'pour', 'sans', 'dans', 'compris', 'compatible', 'fourniture', 'pose', 'module', 'produit', 'equipement', 'ensemble', 'unite']);
const mots = (s: string) => normaliser(s).split(/[^a-z0-9]+/).filter((m) => m.length >= 5 && !MOTS_VIDES.has(m)).map((m) => m.replace(/s$/, ''));

/**
 * Score de compatibilité d'un produit avec un texte : +3 par protocole du produit cité, +3 si la famille est citée, +1 par mot
 * significatif de la désignation retrouvé. Compatible à partir de 3 (au moins un protocole ou la famille).
 */
export function scoreCompatibilite(p: { designation: string; famille: string | null; protocoles: string[] }, texte: string): number {
  const cites = protocolesCites(texte);
  let score = p.protocoles.filter((x) => cites.has(x as Protocole)).length * 3;
  const motsTexte = new Set(mots(texte));
  if (p.famille && mots(p.famille).length && mots(p.famille).every((m) => motsTexte.has(m))) score += 3;
  score += [...new Set(mots(p.designation))].filter((m) => motsTexte.has(m)).length;
  return score;
}
export const SEUIL_COMPATIBLE = 3;

// ─── Stock à la pose d'un numéro de série (migration 048) ──────────────────────────────────────
export const STATUTS_SERIE = ['en_stock', 'pose', 'retourne', 'hors_service'] as const;
export type StatutSerie = typeof STATUTS_SERIE[number];
/**
 * Effet sur le stock d'un n° de série qui prend le statut `cible`, selon que son unité est déjà sortie du stock (`sorti`) :
 *   - « posé » et pas encore sorti → −1 (l'unité sort) ;
 *   - « en stock » ou « retourné » alors qu'elle était sortie → +1 (l'unité revient) ;
 *   - tout le reste (« hors service », ou statut sans changement d'état de sortie) → 0. Jamais deux fois le même mouvement.
 */
export function effetStock(sorti: boolean, cible: StatutSerie): { delta: -1 | 0 | 1; sorti: boolean } {
  if (cible === 'pose') return sorti ? { delta: 0, sorti } : { delta: -1, sorti: true };
  if (cible === 'en_stock' || cible === 'retourne') return sorti ? { delta: 1, sorti: false } : { delta: 0, sorti };
  return { delta: 0, sorti };
}

// ─── Matériel d'une Proposition TRAME ↔ Ma gamme ───────────────────────────────────────────────
/** Catégories du matériel chiffrable d'une variante de la Proposition TRAME (calcul d'implantation inchangé). */
export const CATEGORIES_MATERIEL = ['unite_interieure', 'groupe_exterieur', 'derivation', 'tube', 'relevage', 'regulation'] as const;
export type CategorieMateriel = typeof CATEGORIES_MATERIEL[number];
/**
 * Familles « Ma gamme » compatibles avec chaque catégorie : mots (normalisés, sans accent) dont UN au moins doit figurer dans la
 * famille du produit. La famille est un texte libre saisi par la société (« Climatisation », « Régulation », « Accessoire
 * frigorifique »…) : la correspondance est déclarative, testée, et documentée (docs/DECISIONS.md).
 */
export const FAMILLES_COMPATIBLES: Record<CategorieMateriel, string[]> = {
  unite_interieure: ['climatisation', 'climatiseur', 'pompe a chaleur', 'pac', 'unite interieure', 'split', 'cassette', 'gainable', 'console', 'murale', 'drv', 'vrv', 'vrf', 'cvc'],
  groupe_exterieur: ['climatisation', 'climatiseur', 'pompe a chaleur', 'pac', 'groupe exterieur', 'unite exterieure', 'drv', 'vrv', 'vrf', 'cvc'],
  derivation: ['derivation', 'joint', 'collecteur', 'raccord', 'accessoire frigorifique', 'frigorifique'],
  tube: ['tube', 'cuivre', 'liaison frigorifique', 'frigorifique'],
  relevage: ['relevage', 'condensat', 'pompe de relevage'],
  regulation: ['regulation', 'passerelle', 'gtb', 'supervision', 'thermostat', 'commande', 'telecommande', 'interface'],
};
const motsFamille = (s: string) => ` ${normaliser(s).replace(/[^a-z0-9]+/g, ' ').trim()} `;
/** La famille du produit est-elle compatible avec la catégorie du matériel ? (mot entier ou expression entière) */
export function familleCompatible(famille: string | null, categorie: CategorieMateriel): boolean {
  if (!famille) return false;
  const f = motsFamille(famille);
  return FAMILLES_COMPATIBLES[categorie].some((m) => f.includes(` ${m} `));
}
/**
 * Score d'un produit Ma gamme pour une ligne de matériel : famille compatible (+6, exigée), puis la compatibilité du texte de la
 * ligne (protocoles cités, mots de la désignation : scoreCompatibilite). 0 = non proposé.
 */
/** Mots qui désignent l'AUTRE côté d'un système : une cassette ne remplace jamais un groupe extérieur, et inversement. */
const EXCLUS: Partial<Record<CategorieMateriel, string[]>> = {
  groupe_exterieur: ['cassette', 'gainable', 'murale', 'mural', 'console', 'plafonnier', 'unite interieure'],
  unite_interieure: ['groupe exterieur', 'unite exterieure'],
};
export function scoreMateriel(p: { designation: string; famille: string | null; protocoles: string[]; unite?: string }, categorie: CategorieMateriel, texte: string,
  unite?: string): number {
  if (!familleCompatible(p.famille, categorie)) return 0;
  // Unité de mesure : un produit compté à l'unité ne remplace pas une longueur (bus, tube en mètres), et inversement.
  if (unite && p.unite && motsFamille(p.unite).trim() !== motsFamille(unite).trim()) return 0;
  const d = motsFamille(`${p.designation} ${p.famille ?? ''}`);
  if ((EXCLUS[categorie] ?? []).some((m) => d.includes(` ${m} `))) return 0;
  return 6 + scoreCompatibilite(p, texte);
}

// ─── « On préconise, on propose » (décision du dirigeant du 31/10/2026) ───
// Un produit Ma gamme est PROPOSÉ (en tête de liste s'il le faut), jamais coché, sélectionné ni inséré d'office ; son prix de vente
// conseillé ne s'applique que si l'utilisateur choisit ce produit. Les autres choix (catalogue, prix des fournisseurs, marque demandée
// par le client) restent proposés à égalité, juste à côté. Sinon c'est survendre.
export const LIBELLE_PROPOSE_GAMME = 'Proposé (Ma gamme)';
/** Phrase discrète, visible du bureau seulement (comptes qui modifient les devis). */
export const PHRASE_MARQUE_CLIENT = 'Proposition : le client peut demander une marque précise (garantie, matériel identique à l’existant).';
/** Valeur du choix « matériel de la variante » dans le panneau « Matériel à chiffrer » de la Proposition TRAME. */
export const CHOIX_MODELE = 'modele';
/**
 * Choix initial d'une ligne du « Matériel à chiffrer » : le matériel de la variante ; une ligne d'appoint (sans matériel proposé par la
 * variante) reste « à choisir » (''). JAMAIS un produit Ma gamme, même compatible et classé en tête.
 */
export const choixInitialMateriel = (l: { complement?: boolean }) => (l.complement ? '' : CHOIX_MODELE);
/** Une ligne d'appoint n'est retenue d'office que si elle a une quantité ET un choix explicite : jamais au chargement. */
export const retenueInitialeMateriel = (l: { quantite: string | null; complement?: boolean }) => l.quantite !== null && !l.complement;
